<script setup lang="ts">
import { cn } from '@/shadcn/lib/utils'
import { X } from '@lucide/vue'
import {
  DialogClose,
  DialogContent,
  type DialogContentEmits,
  type DialogContentProps,
  DialogOverlay,
  DialogPortal,
  useForwardPropsEmits,
} from 'reka-ui'
import { computed, type HTMLAttributes } from 'vue'

const props = defineProps<DialogContentProps & { class?: HTMLAttributes['class'] }>()
const emits = defineEmits<DialogContentEmits & {
  'enter': () => void
  'leave': () => void
  'after-enter': () => void
  'after-leave': () => void
}>()

const delegatedProps = computed(() => {
  const { class: _, ...delegated } = props

  return delegated
})

const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<template>
  <DialogPortal>
    <DialogOverlay
      class="tw:fixed tw:inset-0 tw:z-50 tw:xl:grid tw:place-items-center tw:overflow-y-auto tw:bg-black/80 tw:data-[state=open]:animate-in tw:data-[state=closed]:animate-out tw:data-[state=closed]:fade-out-0 tw:data-[state=open]:fade-in-0"
    >
      <DialogContent
        :class="
          cn(
            'tw:relative tw:z-50 tw:grid tw:mx-auto tw:w-full tw:max-w-lg tw:my-3 tw:xl:my-12 tw:gap-4 tw:border tw:border-border tw:bg-background tw:p-6 tw:shadow-lg tw:duration-200 tw:sm:rounded-lg tw:md:w-full',
            props.class,
          )
        "
        v-bind="forwarded"
        @pointer-down-outside="(event) => {
          const originalEvent = event.detail.originalEvent;
          const target = originalEvent.target as HTMLElement;
          if (originalEvent.offsetX > target.clientWidth || originalEvent.offsetY > target.clientHeight) {
            event.preventDefault();
          }
        }"
      >
        <slot />

        <DialogClose
          class="tw:absolute tw:top-4 tw:right-4 tw:p-0.5 tw:transition-colors tw:rounded-md tw:hover:bg-secondary"
        >
          <X class="tw:w-4 tw:h-4" />
          <span class="tw:sr-only">Close</span>
        </DialogClose>
      </DialogContent>
    </DialogOverlay>
  </DialogPortal>
</template>
